/* ============================================================================
   SEARCH V2  (assets/search-v2.js)
   ----------------------------------------------------------------------------
   Vanilla, dependency-free autocomplete for the header search bar.

   - Credentials/config are read at runtime from window.algoliaShopify.config
     (set by the Algolia app's algolia_config.js), so nothing is duplicated.
   - Queries hit Algolia's REST API directly with fetch (one multi-query call
     for products + collections). No JS client library, so the app's pending
     v3 -> v5 client migration does not apply to this surface.
   - Recent searches: localStorage, Amazon-style, free (no Algolia feature).
   - Click events are beaconed to Algolia Insights with the queryID so CTR
     analytics keep working for the new UI.
   - The old autocomplete is disabled by the inline flag flip in
     snippets/search-v2.liquid before this file runs.
   ============================================================================ */
(function () {
  'use strict';

  var root = document.getElementById('search-v2');
  var alg = window.algoliaShopify && algoliaShopify.config;
  if (!root || !alg || !alg.app_id || !alg.search_api_key) return;

  document.body.classList.add('sv2-enabled');

  var PREFIX = alg.index_prefix || '';
  var HOST = 'https://' + alg.app_id + '-dsn.algolia.net';
  var SEARCH_URL = root.getAttribute('data-search-url') || '/search';
  var RECENT_KEY = 'ep-sv2-recent';
  var TOKEN_KEY = 'ep-sv2-token';
  var MAX_RECENT = 8;
  var DEBOUNCE_MS = 160;
  var HITS = 8;

  var form = document.querySelector('.header form[action*="/search"], form.search-bar');
  var input = form && form.querySelector('input[name="q"]');
  if (!form || !input) return;

  /* On the search results page the header search stays fully independent of
     the page's instant-search widget: clicking it opens the normal panel
     (promo / recents / dense results) and Enter navigates to a fresh
     /search?q=. The page's own "Current search" box keeps doing the live
     refinement. Only the current query is carried into the header input, so
     reopening the panel picks up where the visitor is. (An earlier version
     relayed header keystrokes into the ais widget, which fused the two
     boxes into one control - deliberately removed.) */
  if (window.location.pathname.indexOf(SEARCH_URL) === 0) {
    var q0 = '';
    try { q0 = new URLSearchParams(window.location.search).get('q') || ''; } catch (e) {}
    if (q0 && !input.value) input.value = q0;
  }

  var panel = root.querySelector('.sv2__panel');
  var elEmpty = root.querySelector('[data-sv2-empty]');
  var elResults = root.querySelector('[data-sv2-results]');
  var elColls = root.querySelector('[data-sv2-collections]');
  var elHits = root.querySelector('[data-sv2-hits]');
  var elNone = root.querySelector('[data-sv2-none]');
  var elNoneQ = root.querySelector('[data-sv2-none-q]');
  var elAll = root.querySelector('[data-sv2-all]');
  var elAllCount = root.querySelector('[data-sv2-all-count]');
  var elRecentWrap = root.querySelector('[data-sv2-recent]');
  var elRecentList = root.querySelector('[data-sv2-recent-list]');

  var open = false;
  var lockedY = null;
  var trackUntil = 0;
  var timer = null;
  var controller = null;
  var lastQueryID = null;
  var lastQuery = '';
  var cache = {};
  var activeIndex = -1;

  /* ------------------------------ utils ------------------------------ */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* Algolia highlight values are pre-escaped; whitelist only our mark tags */
  function safeHighlight(v) {
    return String(v || '')
      .replace(/</g, '&lt;')
      .replace(/&lt;(\/?)mark>/g, '<$1mark>');
  }
  function money(n) {
    if (n == null || isNaN(n)) return '';
    var s = Number(n).toFixed(2).replace(/\.00$/, '');
    return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function userToken() {
    try {
      var t = localStorage.getItem(TOKEN_KEY);
      if (!t) {
        t = 'sv2-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(TOKEN_KEY, t);
      }
      return t;
    } catch (e) { return 'sv2-anon'; }
  }
  function thumb(url) {
    if (!url) return '';
    /* Shopify CDN image transform via query param (works on /files/ URLs) */
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=120';
  }

  /* ------------------------- nav collection allowlist ------------------------- */
  /* Only collections that are actually linked in the navigation may appear as
     search suggestions (team rule). Derived from the nav DOM at first use, so
     editing the nav automatically updates search - no second list to maintain.
     Falls back to allowing everything if no nav collection links are found. */
  var navAllow = null;
  function navAllowlist() {
    if (navAllow) return navAllow;
    var set = {};
    var links = document.querySelectorAll('.gnav a[href*="/collections/"], #mobile-menu a[href*="/collections/"]');
    for (var i = 0; i < links.length; i++) {
      var m = links[i].getAttribute('href').match(/\/collections\/([^/?#]+)/);
      if (m) set[m[1].toLowerCase()] = true;
    }
    navAllow = set;
    return set;
  }

  /* --------------------------- recent searches --------------------------- */
  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; }
  }
  function saveRecent(q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    try {
      var list = getRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) { /* private mode */ }
  }
  function removeRecent(q) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(getRecent().filter(function (x) { return x !== q; })));
    } catch (e) {}
    renderRecent();
  }
  function renderRecent() {
    if (!elRecentWrap || !elRecentList) return;
    var list = getRecent();
    if (!list.length) { elRecentWrap.hidden = true; return; }
    elRecentWrap.hidden = false;
    elRecentList.innerHTML = list.map(function (q) {
      return '<li>' +
        '<button type="button" class="sv2__recent-term" data-sv2-fill="' + esc(q) + '">' + esc(q) + '</button>' +
        '<button type="button" class="sv2__recent-x" data-sv2-remove="' + esc(q) + '" aria-label="Remove ' + esc(q) + ' from recent searches">✕</button>' +
        '</li>';
    }).join('');
  }

  /* ------------------------------ positioning ------------------------------ */
  function isMobile() { return window.matchMedia('(max-width: 1023px)').matches; }

  /* Theme-safe scroll lock. body{position:fixed} (the classic iOS lock)
     detonates this theme's header/drawer layout, so instead: overflow:hidden
     on html+body for desktop browsers, plus a touchmove blocker for iOS that
     still allows scrolling inside the panel itself. */
  function blockTouch(e) {
    if (!root.contains(e.target)) { e.preventDefault(); return; }
    /* inside the panel: let the panel scroll, but stop rubber-banding through */
    if (panel.scrollHeight <= panel.clientHeight) e.preventDefault();
  }
  function lockScroll() {
    if (lockedY !== null) return;
    lockedY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.addEventListener('touchmove', blockTouch, { passive: false });
  }
  function unlockScroll() {
    if (lockedY === null) return;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.removeEventListener('touchmove', blockTouch, { passive: false });
    lockedY = null;
  }

  function place() {
    if (!open) return;
    var r = form.getBoundingClientRect();
    var top = Math.round(r.bottom + 6);
    root.style.top = top + 'px';
    if (isMobile()) {
      root.style.left = '0px';
      root.style.width = '100%';
      /* keyboard-aware height: cap the sheet to the visual viewport */
      var vvh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      var sheetH = Math.max(160, Math.round(vvh - top));
      panel.style.maxHeight = sheetH + 'px';
      /* fill to the bottom so page content never peeks out beneath the sheet */
      panel.style.height = sheetH + 'px';
    } else {
      panel.style.maxHeight = '';
      panel.style.height = '';
      var width = Math.max(Math.min(r.width, 760), 560);
      var left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      root.style.left = Math.round(left) + 'px';
      root.style.width = Math.round(width) + 'px';
    }
  }

  /* the theme animates the mobile bar into place on open; track its rect for
     a short window instead of listening to scroll (which is locked anyway) */
  function trackPlacement(ms) {
    trackUntil = Date.now() + (ms || 600);
    (function tick() {
      if (!open) return;
      place();
      if (Date.now() < trackUntil) requestAnimationFrame(tick);
    })();
  }

  /* ------------------------------ open / close ------------------------------ */
  function openPanel() {
    if (open) { place(); return; }
    open = true;
    root.hidden = false;
    renderRecent();
    showState(input.value.trim() ? 'results' : 'empty');
    if (input.value.trim()) query(input.value.trim());
    document.body.classList.add('sv2-open');
    if (isMobile()) {
      /* the theme's mobile bar is absolute near the top of the document (no
         sticky header), so opening search while scrolled leaves it far above
         the viewport; bring it into view, then freeze the page under it */
      var r = form.getBoundingClientRect();
      if (r.top < 0 || r.top > 140) {
        var absTop = r.top + (window.scrollY || window.pageYOffset || 0);
        window.scrollTo(0, Math.max(0, Math.round(absTop - 12)));
      }
      lockScroll();
    }
    trackPlacement(700);
    input.setAttribute('aria-expanded', 'true');
  }
  function closePanel() {
    if (!open) return;
    open = false;
    root.hidden = true;
    document.body.classList.remove('sv2-open');
    unlockScroll();
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
  }
  function showState(which) {
    elEmpty.hidden = which !== 'empty';
    elResults.hidden = which !== 'results';
  }

  /* ------------------------------ querying ------------------------------ */
  function query(q) {
    lastQuery = q;
    if (cache[q]) { render(q, cache[q]); return; }
    if (controller) controller.abort();
    controller = ('AbortController' in window) ? new AbortController() : null;

    var body = JSON.stringify({
      requests: [
        {
          indexName: PREFIX + 'products',
          query: q,
          params: 'hitsPerPage=' + HITS + '&distinct=true&clickAnalytics=true' +
            '&highlightPreTag=' + encodeURIComponent('<mark>') +
            '&highlightPostTag=' + encodeURIComponent('</mark>') +
            '&userToken=' + encodeURIComponent(userToken())
        },
        { indexName: PREFIX + 'collections', query: q, params: 'hitsPerPage=12' }
      ]
    });

    fetch(HOST + '/1/indexes/*/queries', {
      method: 'POST',
      body: body,
      signal: controller && controller.signal,
      headers: {
        'X-Algolia-Application-Id': alg.app_id,
        'X-Algolia-API-Key': alg.search_api_key
      }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.results) return;
        cache[q] = data;
        if (q === lastQuery) render(q, data);
      })
      .catch(function () { /* aborted or offline; keep whatever is shown */ });
  }

  /* ------------------------------ ratings ------------------------------ */
  /* Reviews.io writes metafields reviews.rating / reviews.rating_count,
     indexed by the Algolia app as meta.reviews. The rating arrives as a
     JSON-encoded string ('{"scale_min":"1.0","scale_max":"5.0","value":"4.77"}');
     also tolerates a plain object or bare number. */
  function ratingFor(hit) {
    var m = hit.meta && hit.meta.reviews;
    if (!m) return null;
    var raw = m.rating;
    if (typeof raw === 'string' && raw.charAt(0) === '{') {
      try { raw = JSON.parse(raw); } catch (e) { /* fall through */ }
    }
    if (raw && typeof raw === 'object') raw = raw.value;
    var val = parseFloat(raw);
    var count = parseInt(m.rating_count, 10) || 0;
    if (!val || !count) return null;
    return { value: Math.round(val * 10) / 10, count: count };
  }

  /* ------------------------------ chips ------------------------------ */
  function chipsFor(hit) {
    var chips = [];
    var tags = hit.tags || [];
    var soldOut = hit.inventory_available === false;
    var qty = typeof hit.inventory_quantity === 'number' ? hit.inventory_quantity : null;

    /* ERP models built-to-order equipment as zero/negative inventory, so a
       bare inventory_available check mislabels lead-time machines. When the
       indexed leadtime metafield is present it names the real ship window;
       product_type EQUIPMENT stays as the proxy for records the metafield
       hasn't reached. Refurb units are one-offs, so sold means sold. */
    var custom = hit.meta && hit.meta.custom;
    var lead = custom && custom.dropship_leadtime;
    /* 'Default' means "lead time confirmed by our team" (cart wording), not a ship window */
    var leadDefault = lead === 'Default';
    if (leadDefault) lead = null;
    var isEquipment = String(hit.product_type || '').toUpperCase() === 'EQUIPMENT';
    var isRefurb = tags.some(function (t) { return /refurb/i.test(t); });
    /* Pre-orders follow the PDP/cart rule: a future custom.preorder_until
       date. The index's inventory flags are unreliable for these (oversold
       pre-orders index as unavailable), so the date wins over sold-out
       labels; lead time still wins over pre-order, as on the PDP. */
    var preDate = null;
    var until = custom && custom.preorder_until;
    var ymd = until && String(until).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      var d = new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
      if (d > new Date()) preDate = d;
    }
    var isPre = !!preDate || hit.template_suffix === 'pre-order';
    if (soldOut && !isRefurb && lead) {
      chips.push(['info', 'Ships in ' + String(lead).replace(/\bweeks?\b/i, 'wks').replace(/\bdays?\b/i, 'days')]);
    }
    else if (isPre && !lead) {
      chips.push(['blue', preDate ? 'Pre-order \u00b7 ships by ' + preDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Pre-order']);
    }
    else if (soldOut && isEquipment && !isRefurb && !leadDefault) chips.push(['info', 'Built to order']);
    else if (soldOut && !leadDefault) chips.push(['muted', 'Sold out']);
    if (hit.compare_at_price && hit.compare_at_price > hit.price) chips.push(['value', 'Sale']);
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).indexOf('retail--') === 0) { chips.push(['info', 'Volume pricing']); break; }
    }
    if (!soldOut && !isPre && qty !== null && qty > 0 && qty <= 3) chips.push(['urgency', 'Low stock']);
    if (!soldOut && (hit.recently_ordered_count || 0) >= 150) chips.push(['value', 'Bestseller']);
    return chips.slice(0, 2);
  }

  /* ------------------------------ rendering ------------------------------ */
  function render(q, data) {
    var prod = data.results[0] || {};
    var coll = data.results[1] || {};
    var hits = prod.hits || [];
    lastQueryID = prod.queryID || null;
    showState('results');
    activeIndex = -1;
    place();

    /* collection routing chips (data: broad queries click at position 17-25) */
    var seenTitles = {};
    var allow = navAllowlist();
    var allowAll = true;
    for (var k in allow) { allowAll = false; break; }
    var cHits = (coll.hits || []).filter(function (c) {
      if (!c.handle) return false;
      if (!allowAll && !allow[String(c.handle).toLowerCase()]) return false;
      var key = String(c.title || '').toLowerCase().trim();
      if (seenTitles[key]) return false;
      seenTitles[key] = true;
      return true;
    }).slice(0, 3);
    if (cHits.length) {
      elColls.hidden = false;
      elColls.innerHTML = cHits.map(function (c) {
        return '<a class="sv2__coll-chip" href="/collections/' + esc(c.handle) + '">' +
          'in ' + esc(c.title) + '</a>';
      }).join('');
    } else {
      elColls.hidden = true;
      elColls.innerHTML = '';
    }

    if (!hits.length) {
      elHits.innerHTML = '';
      elNone.hidden = false;
      if (elNoneQ) elNoneQ.textContent = q;
      elAll.style.display = 'none';
      return;
    }

    elNone.hidden = true;
    elAll.style.display = '';
    elAll.href = SEARCH_URL + '?q=' + encodeURIComponent(q);
    if (elAllCount) elAllCount.textContent = '(' + (prod.nbHits || hits.length) + ')';

    elHits.innerHTML = hits.map(function (hit, i) {
      var title = hit._highlightResult && hit._highlightResult.title
        ? safeHighlight(hit._highlightResult.title.value)
        : esc(hit.title);
      var img = thumb(hit.product_image || hit.image);
      var multi = (hit.variants_count || 1) > 1;
      var priceMin = typeof hit.variants_min_price === 'number' ? hit.variants_min_price : hit.price;
      var priceMax = typeof hit.variants_max_price === 'number' ? hit.variants_max_price : hit.price;
      var onSale = hit.compare_at_price && hit.compare_at_price > hit.price;
      var priceHtml = (multi && priceMax > priceMin ? '<span class="from">from </span>' : '') + esc(money(priceMin));
      var meta = esc(hit.vendor || '');
      if (multi) meta += (meta ? ' · ' : '') + esc(hit.variants_count + ' options');
      var rating = ratingFor(hit);
      if (rating) meta += (meta ? ' · ' : '') + '<span class="sv2__stars">★</span> ' + esc(rating.value) + ' (' + esc(rating.count) + ')';
      var chips = chipsFor(hit).map(function (c) {
        return '<span class="sv2-chip sv2-chip--' + c[0] + '">' + esc(c[1]) + '</span>';
      }).join('');

      return '<li class="sv2__hit" data-i="' + i + '">' +
        '<a href="/products/' + esc(hit.handle) + '" data-sv2-item data-object-id="' + esc(hit.objectID) + '" data-pos="' + (i + 1) + '">' +
        (img ? '<img src="' + esc(img) + '" alt="" width="52" height="52">' : '<span class="sv2__hit-noimg"></span>') +
        '<span class="sv2__hit-main">' +
          '<p class="sv2__hit-title">' + title + '</p>' +
          (meta ? '<p class="sv2__hit-meta">' + meta + '</p>' : '') +
          (chips ? '<span class="sv2__hit-chips">' + chips + '</span>' : '') +
        '</span>' +
        '<span class="sv2__hit-side">' +
          '<span class="sv2__hit-price' + (onSale ? ' on-sale' : '') + '">' + priceHtml + '</span>' +
          (onSale ? '<span class="sv2__hit-compare">' + esc(money(hit.compare_at_price)) + '</span>' : '') +
        '</span>' +
        '</a></li>';
    }).join('');
  }

  /* ------------------------------ insights ------------------------------ */
  function sendClick(objectID, position) {
    if (!lastQueryID) return;
    var payload = JSON.stringify({
      events: [{
        eventType: 'click',
        eventName: 'Search v2: result clicked',
        index: PREFIX + 'products',
        userToken: userToken(),
        queryID: lastQueryID,
        objectIDs: [String(objectID)],
        positions: [Number(position)]
      }]
    });
    var url = 'https://insights.algolia.io/1/events?x-algolia-application-id=' + alg.app_id +
      '&x-algolia-api-key=' + alg.search_api_key;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
      try { fetch(url, { method: 'POST', body: payload, keepalive: true }); } catch (e) {}
    }
  }

  /* ------------------------------ keyboard nav ------------------------------ */
  function items() { return root.querySelectorAll('[data-sv2-item]'); }
  function setActive(i) {
    var list = items();
    if (!list.length) return;
    activeIndex = (i + list.length) % list.length;
    root.querySelectorAll('.sv2__hit.is-active').forEach(function (el) { el.classList.remove('is-active'); });
    var li = list[activeIndex].closest('.sv2__hit');
    if (li) { li.classList.add('is-active'); li.scrollIntoView({ block: 'nearest' }); }
  }

  /* ------------------------------ events ------------------------------ */
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-haspopup', 'listbox');

  input.addEventListener('focus', openPanel);
  input.addEventListener('click', openPanel);
  input.addEventListener('pointerup', openPanel);

  /* Phones: tapping the header's search GLASS opens the bar, but the theme
     does not focus the input, so nothing summoned the panel and the user saw
     a bare bar (the reported no-onclick-state sessions). Follow the theme's
     reveal animation and put the user straight into the search state; if the
     same toggle just HID the bar while the panel was open, close the panel. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-action="toggle-search"]')) return;
    var tries = 0;
    (function settle() {
      tries++;
      var visible = input.getBoundingClientRect().height > 0;
      if (visible) {
        try { input.focus(); } catch (err) {}
        openPanel(); /* belt: focus events are suppressed in some webviews */
      } else if (tries < 15) {
        setTimeout(settle, 40);
      } else if (open) {
        closePanel(); /* toggle hid the bar; do not leave a floating panel */
      }
    })();
  });

  input.addEventListener('input', function () {
    var q = input.value.trim();
    openPanel();
    clearTimeout(timer);
    if (!q) { showState('empty'); renderRecent(); return; }
    timer = setTimeout(function () { showState('results'); query(q); }, DEBOUNCE_MS);
  });

  input.addEventListener('keydown', function (e) {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0) {
        var el = items()[activeIndex];
        if (el) { saveRecent(input.value); sendClick(el.getAttribute('data-object-id'), el.getAttribute('data-pos')); window.location.assign(el.href); }
      } else {
        goToResults(input.value);
      }
    } else if (e.key === 'Escape') { closePanel(); input.blur(); }
  });

  /* Own the submit end-to-end: other scripts can trigger form.submit() (which
     skips submit events entirely) or race the input's value, which is how
     users were landing on /search with an empty q. Serialize the form's own
     hidden params, force q in, and navigate. */
  function goToResults(q) {
    q = (q || '').trim();
    if (!q || q === input.getAttribute('placeholder')) { input.focus(); return; }
    saveRecent(q);
    var params;
    try { params = new URLSearchParams(new FormData(form)); }
    catch (err) { params = new URLSearchParams(); }
    params.set('q', q);
    window.location.assign(SEARCH_URL + '?' + params.toString());
  }
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    goToResults(input.value);
  });

  root.addEventListener('click', function (e) {
    var fill = e.target.closest('[data-sv2-fill]');
    if (fill) {
      input.value = fill.getAttribute('data-sv2-fill');
      input.focus();
      showState('results');
      query(input.value);
      return;
    }
    var rm = e.target.closest('[data-sv2-remove]');
    if (rm) { removeRecent(rm.getAttribute('data-sv2-remove')); return; }
    var clearAll = e.target.closest('[data-sv2-clear-recent]');
    if (clearAll) {
      try { localStorage.removeItem(RECENT_KEY); } catch (err) {}
      renderRecent();
      return;
    }
    var item = e.target.closest('[data-sv2-item]');
    if (item) {
      saveRecent(lastQuery);
      sendClick(item.getAttribute('data-object-id'), item.getAttribute('data-pos'));
      return; /* let the anchor navigate */
    }
    if (e.target.closest('[data-sv2-all]')) saveRecent(lastQuery);
  });

  var themeWrapper = form.closest('.header__search-bar-wrapper');
  document.addEventListener('click', function (e) {
    if (!open) return;
    if (e.target.closest('[data-sv2-close]')) { closePanel(); input.blur(); return; }
    /* the theme's own mobile Close should also close the panel. Its search
       GLASS toggle must NOT: tapping it opens the bar and focuses the input,
       and closing here would fight that open (white-takeover-with-no-panel). */
    if (e.target.closest('[data-action="unfix-search"]')) { closePanel(); return; }
    if (e.target.closest('[data-action="toggle-search"]')) return;
    if (root.contains(e.target) || form.contains(e.target)) return;
    /* a tap on the theme's fixed-mode white backdrop (the wrapper outside the
       form) means "get me out": close the panel AND the theme's fixed mode */
    if (themeWrapper && themeWrapper.contains(e.target)) {
      closePanel();
      var unfix = document.querySelector('[data-action="unfix-search"]');
      if (unfix) unfix.click();
      return;
    }
    closePanel();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePanel(); });

  window.addEventListener('resize', function () { if (open) { place(); if (!isMobile()) unlockScroll(); } });
  window.addEventListener('scroll', function () { if (open && !isMobile()) place(); }, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () { if (open) place(); });
  }
})();
